import { supabase } from '../lib/supabase'
import { supabaseAdmin } from '../lib/supabaseAdmin'

export interface AnalyticsData {
  totalUsers: number
  activeUsers: number
  newUsersToday: number
  totalRevenue: number
  monthlyRevenue: number
  totalShoots: number
  totalInvoices: number
  bugReports: {
    total: number
    open: number
    critical: number
  }
  userGrowth: number
  revenueGrowth: number
}

export interface UserAnalytics {
  id: string
  email: string
  firstName: string
  lastName: string
  createdAt: string
  lastLoginAt?: string
  subscriptionStatus: string
  role: string
  totalShoots: number
  totalInvoices: number
  totalRevenue: number
}

export interface RevenueAnalytics {
  date: string
  revenue: number
  subscriptions: number
  upgrades: number
  cancellations: number
}

class AdminAnalyticsService {
  async getOverviewAnalytics(timeRange: '7d' | '30d' | '90d' = '30d'): Promise<AnalyticsData> {
    if (!supabaseAdmin) {
      throw new Error('Supabase admin client is not configured')
    }

    try {
      console.log('🔍 Fetching overview analytics with admin client...')
      const now = new Date()
      // const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
      // const startDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000))

      // Get total users using admin client
      const { count: totalUsers, error: usersError } = await supabaseAdmin
        .from('profiles')
        .select('*', { count: 'exact', head: true })

      if (usersError) {
        console.error('Error fetching total users:', usersError)
      } else {
        console.log('✅ Total users found:', totalUsers)
      }

      // Get users created today using admin client
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const { count: newUsersToday, error: newUsersError } = await supabaseAdmin
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', todayStart.toISOString())

      if (newUsersError) {
        console.error('Error fetching new users today:', newUsersError)
      } else {
        console.log('✅ New users today:', newUsersToday)
      }

      // Get total shoots using admin client
      const { count: totalShoots, error: shootsError } = await supabaseAdmin
        .from('shoots')
        .select('*', { count: 'exact', head: true })

      if (shootsError) {
        console.error('Error fetching total shoots:', shootsError)
      }

      // Get total invoices using admin client
      const { count: totalInvoices, error: invoicesError } = await supabaseAdmin
        .from('invoices')
        .select('*', { count: 'exact', head: true })

      if (invoicesError) {
        console.error('Error fetching total invoices:', invoicesError)
      }

      // Get bug reports using admin client
      const { count: totalBugReports, error: bugReportsError } = await supabaseAdmin
        .from('bug_reports')
        .select('*', { count: 'exact', head: true })

      if (bugReportsError) {
        console.error('Error fetching total bug reports:', bugReportsError)
      }

      const { count: openBugReports, error: openBugReportsError } = await supabaseAdmin
        .from('bug_reports')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'open')

      if (openBugReportsError) {
        console.error('Error fetching open bug reports:', openBugReportsError)
      }

      const { count: criticalBugReports, error: criticalBugReportsError } = await supabaseAdmin
        .from('bug_reports')
        .select('*', { count: 'exact', head: true })
        .eq('severity', 'critical')
        .eq('status', 'open')

      if (criticalBugReportsError) {
        console.error('Error fetching critical bug reports:', criticalBugReportsError)
      }

      // Calculate growth rates (mock data - would calculate from historical data)
      // Growth rates vary based on time range for demo purposes
      const baseGrowth = timeRange === '7d' ? 2 : timeRange === '30d' ? 8 : 15
      const userGrowth = Math.floor(Math.random() * 10) + baseGrowth

      // Active users (mock data - would track login activity)
      const activeUsers = Math.floor((totalUsers || 0) * 0.3)

      return {
        totalUsers: totalUsers || 0,
        activeUsers,
        newUsersToday: newUsersToday || 0,
        totalRevenue: 0,
        monthlyRevenue: 0,
        totalShoots: totalShoots || 0,
        totalInvoices: totalInvoices || 0,
        bugReports: {
          total: totalBugReports || 0,
          open: openBugReports || 0,
          critical: criticalBugReports || 0
        },
        userGrowth,
        revenueGrowth: 0
      }
    } catch (error) {
      console.error('Error fetching analytics:', error)
      // Return empty data if there's an error
      return {
        totalUsers: 0,
        activeUsers: 0,
        newUsersToday: 0,
        totalRevenue: 0,
        monthlyRevenue: 0,
        totalShoots: 0,
        totalInvoices: 0,
        bugReports: {
          total: 0,
          open: 0,
          critical: 0
        },
        userGrowth: 0,
        revenueGrowth: 0
      }
    }
  }

  async getUserAnalytics(limit: number = 50, offset: number = 0): Promise<UserAnalytics[]> {
    if (!supabaseAdmin) {
      throw new Error('Supabase admin client is not configured')
    }

    try {
      console.log('Fetching user analytics with admin client...')
      const { data: users, error } = await supabaseAdmin
        .from('profiles')
        .select(`
          id,
          email,
          first_name,
          last_name,
          created_at,
          subscription_status,
          role
        `)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (error) {
        console.error('Error fetching users:', error)
        throw error
      }

      console.log(`Found ${users?.length || 0} users`)

      // For each user, get their shoots and invoices count
      const userAnalytics = await Promise.all(
        (users || []).map(async (user) => {
          if (!supabaseAdmin) {
            return {
              id: user.id,
              email: user.email,
              firstName: user.first_name,
              lastName: user.last_name,
              createdAt: user.created_at,
              subscriptionStatus: user.subscription_status,
              role: user.role || 'user',
              totalShoots: 0,
              totalInvoices: 0,
              totalRevenue: 0
            }
          }

          const [shootsResult, invoicesResult] = await Promise.all([
            supabaseAdmin
              .from('shoots')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', user.id),
            supabaseAdmin
              .from('invoices')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', user.id)
          ])

          return {
            id: user.id,
            email: user.email,
            firstName: user.first_name,
            lastName: user.last_name,
            createdAt: user.created_at,
            subscriptionStatus: user.subscription_status,
            role: user.role || 'user',
            totalShoots: shootsResult.count || 0,
            totalInvoices: invoicesResult.count || 0,
            totalRevenue: 0 // Revenue tracking not implemented yet
          }
        })
      )

      return userAnalytics
    } catch (error) {
      console.error('Error fetching user analytics:', error)
      return []
    }
  }

  async getRevenueAnalytics(timeRange: '7d' | '30d' | '90d' = '30d'): Promise<RevenueAnalytics[]> {
    // Revenue analytics not implemented yet
    // In a real implementation, this would fetch from your payment processor
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
    const data: RevenueAnalytics[] = []
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      
      data.push({
        date: date.toISOString().split('T')[0],
        revenue: 0,
        subscriptions: 0,
        upgrades: 0,
        cancellations: 0
      })
    }
    
    return data
  }

  async getSystemMetrics() {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      // Get database size and performance metrics
      // Note: These would typically come from your database monitoring tools
      return {
        databaseSize: '2.4 GB',
        apiResponseTime: '145ms',
        uptime: '99.9%',
        activeConnections: 23,
        errorRate: '0.1%',
        lastBackup: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() // 2 hours ago
      }
    } catch (error) {
      console.error('Error fetching system metrics:', error)
      return {
        databaseSize: 'Unknown',
        apiResponseTime: 'Unknown',
        uptime: 'Unknown',
        activeConnections: 0,
        errorRate: 'Unknown',
        lastBackup: 'Unknown'
      }
    }
  }

  async getTopUsers(limit: number = 10) {
    if (!supabaseAdmin) {
      throw new Error('Supabase admin client is not configured')
    }

    try {
      console.log('🔍 Fetching top users with admin client...')
      // Get users with most shoots using admin client
      const { data: users, error } = await supabaseAdmin
        .from('profiles')
        .select(`
          id,
          email,
          first_name,
          last_name,
          subscription_status
        `)
        .limit(limit)

      if (error) {
        console.error('Error fetching users for top users:', error)
        throw error
      }

      console.log(`✅ Found ${users?.length || 0} users for top users analysis`)

      // Get shoot counts for each user using admin client
      const usersWithCounts = await Promise.all(
        (users || []).map(async (user) => {
          if (!supabaseAdmin) {
            return {
              ...user,
              shootCount: 0
            }
          }

          const { count, error: shootCountError } = await supabaseAdmin
            .from('shoots')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)

          if (shootCountError) {
            console.error(`Error fetching shoot count for user ${user.id}:`, shootCountError)
          }

          return {
            ...user,
            shootCount: count || 0
          }
        })
      )

      // Sort by shoot count and return top users
      const topUsers = usersWithCounts
        .sort((a, b) => b.shootCount - a.shootCount)
        .slice(0, limit)

      console.log('✅ Top users calculated:', topUsers.length)
      return topUsers
    } catch (error) {
      console.error('Error fetching top users:', error)
      return []
    }
  }
}

export const adminAnalyticsService = new AdminAnalyticsService() 